(() => {
  const registry = window.NipponVinoWineAtlasRenderers = window.NipponVinoWineAtlasRenderers || {};
  const Base = registry.Base;
  if (!Base) return;

  const CURATED_REGION_IDS = new Set([
    'yamanashi',
    'nagano',
    'hokkaido',
    'yamagata',
    'iwate',
    'ibaraki',
    'niigata',
    'osaka',
    'okayama',
    'shimane',
  ]);

  const ISLAND_GROUPS = Object.freeze({
    hokkaido: new Set(['01']),
    shikoku: new Set(['36', '37', '38', '39']),
    kyushu: new Set(['40', '41', '42', '43', '44', '45', '46']),
  });

  const TOKYO = Object.freeze({
    name: 'Tokio', longitude: 139.69171, latitude: 35.6895,
  });

  const CAMERA_PRESETS = Object.freeze({
    desktop: Object.freeze({
      japan: Object.freeze({ width: 1200, height: 690 }),
      region: Object.freeze({ width: 560, height: 322 }),
      detail: Object.freeze({ width: 390, height: 224 }),
      focusX: 0.36,
      focusY: 0.52,
    }),
    tablet: Object.freeze({
      japan: Object.freeze({ width: 1200, height: 690 }),
      region: Object.freeze({ width: 560, height: 322 }),
      detail: Object.freeze({ width: 390, height: 224 }),
      focusX: 0.5,
      focusY: 0.52,
    }),
    mobile: Object.freeze({
      japan: Object.freeze({ width: 420, height: 310 }),
      region: Object.freeze({ width: 245, height: 181 }),
      detail: Object.freeze({ width: 176, height: 130 }),
      focusX: 0.5,
      focusY: 0.52,
    }),
  });

  class IsometricArchipelagoA2Renderer extends Base {
    mount(container, data, state = {}) {
      super.mount(container, data, state);
      this.mode = state.mode || 'shop';
      this.showCities = Boolean(state.showCities);
      this.cityMarkers = [];
      this.render();
      this.update(this.matchingIds, state);
      this.setLevel(this.level, { animate: false });
    }

    getLayout() {
      const width = this.mapCanvas?.clientWidth || window.innerWidth;
      if (width <= 600) {
        return {
          name: 'mobile', width: 420, height: 310,
          frame: { x: 9, y: 22, width: 402, height: 252 },
          baseDepth: 3.2,
          extrusionX: 0.14,
          elevations: {
            japan: { wine: 5.5, shop: 11, hover: 4, selected: 21, selectedShopBonus: 3 },
            region: { wine: 3.2, shop: 6, hover: 2.8, selected: 14, selectedShopBonus: 2 },
            detail: { wine: 2.2, shop: 4, hover: 2, selected: 9.5, selectedShopBonus: 1.5 },
          },
        };
      }
      return {
        name: width <= 900 ? 'tablet' : 'desktop', width: 1200, height: 690,
        frame: { x: 64, y: 42, width: 1072, height: 566 },
        baseDepth: 4,
        extrusionX: 0.18,
        elevations: {
          japan: { wine: 7, shop: 13, hover: 5, selected: 24, selectedShopBonus: 3 },
          region: { wine: 3.5, shop: 6.5, hover: 3, selected: 15.5, selectedShopBonus: 2 },
          detail: { wine: 2.4, shop: 4.2, hover: 2, selected: 10.5, selectedShopBonus: 1.5 },
        },
      };
    }

    polygonArea(polygon) {
      const ring = polygon?.[0] || [];
      let area = 0;
      for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index, index += 1) {
        area += (Number(ring[previous]?.[0]) * Number(ring[index]?.[1]))
          - (Number(ring[index]?.[0]) * Number(ring[previous]?.[1]));
      }
      return Math.abs(area / 2);
    }

    prepareFeatures() {
      return this.features
        .filter((feature) => String(feature?.properties?.code || '').padStart(2, '0') !== '47')
        .map((feature) => {
          const polygons = feature.geometry?.type === 'Polygon'
            ? [feature.geometry.coordinates]
            : feature.geometry?.coordinates || [];
          const mainland = [...polygons].sort((a, b) => this.polygonArea(b) - this.polygonArea(a))[0];
          return {
            ...feature,
            geometry: { type: 'Polygon', coordinates: mainland || [] },
          };
        });
    }

    islandGroup(code) {
      if (ISLAND_GROUPS.hokkaido.has(code)) return 'hokkaido';
      if (ISLAND_GROUPS.shikoku.has(code)) return 'shikoku';
      if (ISLAND_GROUPS.kyushu.has(code)) return 'kyushu';
      return 'honshu';
    }

    createCompactProjector(features, frame, layoutName) {
      const mercator = ([longitude, latitude]) => {
        const clipped = Math.max(-85, Math.min(85, Number(latitude)));
        return [
          Number(longitude) * Math.PI / 180,
          -Math.log(Math.tan((Math.PI / 4) + ((clipped * Math.PI / 180) / 2))),
        ];
      };
      const rotation = (layoutName === 'mobile' ? 9 : 13) * Math.PI / 180;
      const tilt = layoutName === 'mobile' ? 0.64 : 0.57;
      const cosine = Math.cos(rotation);
      const sine = Math.sin(rotation);
      const affine = ([x, y]) => [
        (x * cosine) - (y * sine),
        ((x * sine) + (y * cosine)) * tilt,
      ];
      const rawByGroup = new Map();
      const rawEntries = [];
      features.forEach((feature) => {
        const code = String(feature.properties?.code || '').padStart(2, '0');
        const group = this.islandGroup(code);
        this.eachCoordinate(feature, (coordinate) => {
          const point = affine(mercator(coordinate));
          rawEntries.push({ code, group, point });
          if (!rawByGroup.has(group)) rawByGroup.set(group, []);
          rawByGroup.get(group).push(point);
        });
      });
      const allXs = rawEntries.map((entry) => entry.point[0]);
      const allYs = rawEntries.map((entry) => entry.point[1]);
      const spanX = Math.max(...allXs) - Math.min(...allXs);
      const spanY = Math.max(...allYs) - Math.min(...allYs);
      const centers = new Map([...rawByGroup].map(([group, points]) => [group, {
        x: (Math.min(...points.map((point) => point[0])) + Math.max(...points.map((point) => point[0]))) / 2,
        y: (Math.min(...points.map((point) => point[1])) + Math.max(...points.map((point) => point[1]))) / 2,
      }]));
      const transforms = layoutName === 'mobile'
        ? {
          honshu: { scale: 1, dx: 0, dy: 0 },
          hokkaido: { scale: 1.07, dx: -spanX * 0.032, dy: spanY * 0.068 },
          shikoku: { scale: 1.01, dx: spanX * 0.009, dy: -spanY * 0.014 },
          kyushu: { scale: 1, dx: spanX * 0.023, dy: -spanY * 0.022 },
        }
        : {
          honshu: { scale: 1, dx: 0, dy: 0 },
          hokkaido: { scale: 1.09, dx: -spanX * 0.038, dy: spanY * 0.074 },
          shikoku: { scale: 1.015, dx: spanX * 0.01, dy: -spanY * 0.016 },
          kyushu: { scale: 1, dx: spanX * 0.026, dy: -spanY * 0.025 },
        };
      const transformRaw = (point, code) => {
        const group = this.islandGroup(code);
        const transform = transforms[group];
        const center = centers.get(group);
        return [
          center.x + ((point[0] - center.x) * transform.scale) + transform.dx,
          center.y + ((point[1] - center.y) * transform.scale) + transform.dy,
        ];
      };
      const transformed = rawEntries.map((entry) => transformRaw(entry.point, entry.code));
      const xs = transformed.map((point) => point[0]);
      const ys = transformed.map((point) => point[1]);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const padding = layoutName === 'mobile' ? 16 : 22;
      const content = {
        x: frame.x + padding,
        y: frame.y + padding,
        width: Math.max(1, frame.width - (padding * 2)),
        height: Math.max(1, frame.height - (padding * 2)),
      };
      const scale = Math.min(
        content.width / Math.max(maxX - minX, 0.0001),
        content.height / Math.max(maxY - minY, 0.0001)
      );
      const offsetX = content.x + ((content.width - ((maxX - minX) * scale)) / 2);
      const offsetY = content.y + ((content.height - ((maxY - minY) * scale)) / 2);
      return (coordinate, code = '') => {
        const transformedPoint = transformRaw(affine(mercator(coordinate)), String(code).padStart(2, '0'));
        return [
          offsetX + ((transformedPoint[0] - minX) * scale),
          offsetY + ((transformedPoint[1] - minY) * scale),
        ];
      };
    }

    projectorForFeature(feature) {
      const code = String(feature?.properties?.code || '').padStart(2, '0');
      return (coordinate) => this.projector(coordinate, code);
    }

    featureSidePath(feature, projector, depth) {
      const polygons = feature.geometry?.type === 'Polygon'
        ? [feature.geometry.coordinates]
        : feature.geometry?.coordinates || [];
      return polygons.map((polygon) => {
        const ring = polygon?.[0] || [];
        const projected = ring.map(projector);
        const signedArea = projected.reduce((area, point, index) => {
          const next = projected[(index + 1) % projected.length];
          return area + ((point[0] * next[1]) - (next[0] * point[1]));
        }, 0);
        const extrusionX = depth * (this.layout?.extrusionX || 0);
        return projected.slice(0, -1).map(([x1, y1], index) => {
          const [x2, y2] = projected[index + 1] || projected[0];
          const cross = ((x2 - x1) * depth) - ((y2 - y1) * extrusionX);
          const facesViewer = signedArea > 0 ? cross < 0 : cross > 0;
          if (!facesViewer || Math.abs(cross) < 0.08) return '';
          return `M${x1.toFixed(2)},${y1.toFixed(2)}L${x2.toFixed(2)},${y2.toFixed(2)}L${(x2 + extrusionX).toFixed(2)},${(y2 + depth).toFixed(2)}L${(x1 + extrusionX).toFixed(2)},${(y1 + depth).toFixed(2)}Z`;
        }).join('');
      }).join('');
    }

    elevationProfile() {
      return this.layout?.elevations?.[this.level] || this.layout?.elevations?.japan || {
        wine: 0, shop: 0, hover: 0, selected: 0, selectedShopBonus: 0,
      };
    }

    elevationFor(regionId, state = 'rest') {
      const profile = this.elevationProfile();
      const region = this.controller.regionById.get(regionId);
      const shop = (region?.availableWines || 0) > 0;
      const rest = shop ? profile.shop : profile.wine;
      if (state === 'hover') return rest + profile.hover;
      if (state === 'selected') return profile.selected + (shop ? profile.selectedShopBonus : 0);
      return rest;
    }

    projectedElevation(elevation) {
      return {
        x: elevation * (this.layout?.extrusionX || 0),
        y: elevation,
      };
    }

    render() {
      const layout = this.getLayout();
      this.layout = layout;
      this.layoutName = layout.name;
      this.controller.dataset.atlasLayout = layout.name;
      this.controller.dataset.mapRenderer = 'isometric-a-2';
      this.geometryById.clear();
      this.featureById = new Map();
      this.surfaceById = new Map();
      this.preparedFeatures = this.prepareFeatures();
      this.projector = this.createCompactProjector(this.preparedFeatures, layout.frame, layout.name);

      const svg = this.createSvg(
        layout.width,
        layout.height,
        'Isometrischer Archipel der japanischen Weinregionen',
        'Kompakte SVG-Karte der vier japanischen Hauptinseln. Zehn kuratierte Weinregionen können mit Maus, Touch, Enter oder Leertaste geöffnet werden; die übrigen Präfekturen dienen ausschließlich der Orientierung.'
      );
      svg.classList.add('wine-atlas-isometric-map');
      svg.dataset.atlasLevel = this.level;

      const defs = this.createElement('defs');
      const filter = this.createElement('filter', {
        id: `atlas-ground-shadow-${this.controller.id}`, x: '-20%', y: '-30%', width: '150%', height: '180%',
      });
      filter.appendChild(this.createElement('feGaussianBlur', { stdDeviation: layout.name === 'mobile' ? 2.6 : 4.2 }));
      defs.appendChild(filter);
      svg.appendChild(defs);

      const ground = this.createElement('g', { class: 'isometric-ground', 'aria-hidden': 'true' });
      const geography = this.createElement('g', { class: 'isometric-geography' });
      this.preparedFeatures.forEach((feature) => {
        const item = this.regionForFeature(feature);
        const interactive = Boolean(item.region && CURATED_REGION_IDS.has(item.id));
        const featureProjector = this.projectorForFeature(feature);
        const d = this.featurePath(feature, featureProjector);
        const bounds = this.featureBounds(feature, featureProjector);
        if (interactive) {
          this.featureById.set(item.id, feature);
          if (bounds) this.geometryById.set(item.id, bounds);
        }

        ground.appendChild(this.createElement('path', { class: 'isometric-ground-piece', d }));
        const group = this.createElement('g', {
          class: `isometric-prefecture ${interactive ? 'atlas-curated atlas-profiled' : 'atlas-context'}`,
          'data-prefecture-code': item.code,
        });
        const shadow = this.createElement('path', { class: 'isometric-prefecture-shadow', d, 'aria-hidden': 'true' });
        const lift = this.createElement('g', { class: 'isometric-prefecture-lift' });
        const baseSide = this.createElement('path', {
          class: 'isometric-prefecture-side isometric-prefecture-side--base',
          d: this.featureSidePath(feature, featureProjector, layout.baseDepth),
          'aria-hidden': 'true',
        });
        const hoverSide = this.createElement('path', {
          class: 'isometric-prefecture-side isometric-prefecture-side--hover',
          d: this.featureSidePath(feature, featureProjector, layout.baseDepth),
          'aria-hidden': 'true',
        });
        const selectedSide = this.createElement('path', {
          class: 'isometric-prefecture-side isometric-prefecture-side--selected',
          d: this.featureSidePath(feature, featureProjector, layout.baseDepth),
          'aria-hidden': 'true',
        });
        lift.append(
          baseSide,
          hoverSide,
          selectedSide,
          this.createElement('path', { class: 'isometric-prefecture-top', d, 'aria-hidden': 'true' })
        );
        if (interactive) {
          this.surfaceById.set(item.id, {
            group, feature, projector: featureProjector, baseSide, hoverSide, selectedSide,
          });
          lift.appendChild(this.createElement('path', { class: 'isometric-prefecture-hit', d, 'aria-hidden': 'true' }));
          this.bindInteractive(group, feature);
        } else {
          group.setAttribute('aria-hidden', 'true');
        }
        group.append(shadow, lift);
        geography.appendChild(group);
      });

      this.placesLayer = this.renderCities(layout);
      this.selectionPlacesLayer = this.createElement('g', { class: 'isometric-selection-places', 'aria-hidden': 'true' });
      this.regionLabel = this.renderRegionLabel();
      svg.append(ground, geography, this.placesLayer, this.selectionPlacesLayer, this.regionLabel);

      this.container.replaceChildren(svg);
      this.container.setAttribute('aria-busy', 'false');
      this.svg = svg;
      this.originalViewBox = { x: 0, y: 0, width: layout.width, height: layout.height };
      this.japanViewBox = { ...this.originalViewBox };
      this.bindPointerHitResolution();
      this.syncElevations();
      this.updateRegionLabel();
      this.syncCityVisibility();
    }

    bindPointerHitResolution() {
      const resolveHit = (event) => {
        if (!event.detail || !event.target.closest?.('.isometric-prefecture.atlas-curated')) return;
        try {
          const matrix = this.svg.getScreenCTM()?.inverse();
          if (!matrix) return;
          const point = this.svg.createSVGPoint();
          point.x = event.clientX;
          point.y = event.clientY;
          const local = point.matrixTransform(matrix);
          let nearest = { id: '', distance: Number.POSITIVE_INFINITY };
          this.geometryById.forEach((bounds, id) => {
            const dx = local.x - (bounds.x + (bounds.width / 2));
            const dy = local.y - (bounds.y + (bounds.height / 2));
            const distance = (dx * dx) + (dy * dy);
            if (distance < nearest.distance) nearest = { id, distance };
          });
          const feature = this.featureById.get(nearest.id);
          if (!feature) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.selectFeature(feature, { focusDetail: false });
        } catch (error) {
          // The region's own click handler remains available as a safe fallback.
        }
      };
      this.svg.addEventListener('click', resolveHit, true);
      this.listeners.push([this.svg, 'click', resolveHit]);
    }

    regionCode(regionId) {
      return String(this.controller.regionById.get(regionId)?.prefCode || '').padStart(2, '0');
    }

    createCityMarker(place) {
      const code = place.prefCode || this.regionCode(place.regionId);
      const [x, y] = this.projector([Number(place.point.longitude), Number(place.point.latitude)], code);
      const mobile = this.layout?.name === 'mobile';
      const rawDx = Number(place.point.labelDx) || 1;
      const rawDy = Number(place.point.labelDy) || -1;
      const dx = (rawDx < 0 ? -1 : 1) * (mobile ? 7 : 9);
      const dy = (rawDy < 0 ? -1 : 1) * (mobile ? 8 : 9);
      const group = this.createElement('g', {
        class: `isometric-city isometric-city--${place.role}`,
        'data-city-role': place.role,
        'data-city-name': place.name,
        'aria-hidden': 'true',
      });
      if (place.regionId) group.setAttribute('data-region-ids', place.regionId);
      group.append(
        this.createElement('circle', { class: 'isometric-city-ring', cx: x, cy: y, r: place.role === 'tokyo' ? 3.4 : 2.8 }),
        this.createElement('circle', { class: 'isometric-city-point', cx: x, cy: y, r: place.role === 'tokyo' ? 2 : 1.55 })
      );
      const label = this.createElement('text', {
        class: 'isometric-city-label', x: x + dx, y: y + dy,
        'text-anchor': dx < 0 ? 'end' : 'start',
      });
      label.textContent = place.name;
      group.appendChild(label);
      this.cityMarkers.push({ ...place, group, label, x, y, dx, dy });
      return group;
    }

    renderCities() {
      const layer = this.createElement('g', { class: 'isometric-cities', 'aria-label': 'Geografisch projizierte Städte' });
      const places = [{ name: TOKYO.name, point: TOKYO, prefCode: '13', role: 'tokyo' }];
      this.regions.forEach((region) => {
        if (!CURATED_REGION_IDS.has(region.id) || !region.atlas?.capital) return;
        places.push({
          name: region.atlas.capital.name,
          point: region.atlas.capital,
          regionId: region.id,
          role: 'capital',
        });
      });
      places
        .filter((place) => Number.isFinite(Number(place.point?.longitude)) && Number.isFinite(Number(place.point?.latitude)))
        .forEach((place) => layer.appendChild(this.createCityMarker(place)));
      return layer;
    }

    renderRegionLabel() {
      const layer = this.createElement('g', {
        class: 'isometric-region-label',
        'aria-hidden': 'true',
      });
      layer.append(
        this.createElement('rect', {
          class: 'isometric-region-label__plaque', rx: 2,
        })
      );
      const name = this.createElement('text', {
        class: 'isometric-region-label__name',
      });
      const badge = this.createElement('text', {
        class: 'isometric-region-label__badge',
      });
      layer.append(name, badge);
      return layer;
    }

    updateRegionLabel() {
      if (!this.regionLabel) return;
      const regionId = this.selectedId || (this.mode === 'shop' ? 'yamanashi' : '');
      const region = this.controller.regionById.get(regionId);
      const bounds = this.geometryById.get(regionId);
      const visible = Boolean(region && bounds);
      this.regionLabel.classList.toggle('is-visible', visible);
      this.regionLabel.setAttribute('aria-hidden', String(!visible));
      if (!visible) {
        this.regionLabel.removeAttribute('data-region-ids');
        this.regionLabelBox = null;
        return;
      }
      this.regionLabel.setAttribute('data-region-ids', regionId);
      this.regionLabel.classList.toggle('is-available', region.availableWines > 0);
      const compact = this.layout?.name === 'mobile';
      const hasBadge = region.availableWines > 0;
      const width = Math.max(compact ? 62 : 72, (region.name.length * (compact ? 5.2 : 6.2)) + 18);
      const height = hasBadge ? (compact ? 28 : 32) : (compact ? 18 : 21);
      const anchorX = bounds.x + (bounds.width / 2);
      const anchorY = bounds.y + (bounds.height / 2);
      const x = anchorX + (compact ? 8 : 11);
      const y = anchorY - height - (compact ? 7 : 9);
      const plaque = this.regionLabel.querySelector('.isometric-region-label__plaque');
      const name = this.regionLabel.querySelector('.isometric-region-label__name');
      const badge = this.regionLabel.querySelector('.isometric-region-label__badge');
      plaque?.setAttribute('x', x);
      plaque?.setAttribute('y', y);
      plaque?.setAttribute('width', width);
      plaque?.setAttribute('height', height);
      name?.setAttribute('x', x + 7);
      name?.setAttribute('y', y + (compact ? 11 : 13));
      if (name) name.textContent = region.name;
      badge?.setAttribute('x', x + 7);
      badge?.setAttribute('y', y + height - (compact ? 5 : 6));
      badge?.classList.toggle('is-hidden', !hasBadge);
      if (badge) badge.textContent = hasBadge ? 'IM SORTIMENT' : '';
      this.regionLabelBox = { x, y, width, height };
    }

    renderSelectionPlaces() {
      if (!this.selectionPlacesLayer) return;
      this.cityMarkers = this.cityMarkers.filter((marker) => marker.role !== 'orientation');
      this.selectionPlacesLayer.replaceChildren();
      const region = this.controller.regionById.get(this.selectedId);
      if (!region?.atlas || this.level !== 'detail' || !this.showCities) return;
      const points = region.atlas.additionalPoints || [];
      points.forEach((point, index) => {
        if (!Number.isFinite(Number(point?.longitude)) || !Number.isFinite(Number(point?.latitude))) return;
        const place = {
          name: point.name,
          point,
          regionId: region.id,
          role: 'orientation',
        };
        this.selectionPlacesLayer.appendChild(this.createCityMarker(place));
      });
    }

    update(matchingIds, state = {}) {
      super.update(matchingIds, state);
      this.mode = state.mode || this.mode || 'shop';
      if (typeof state.showCities === 'boolean') this.showCities = state.showCities;
      this.svg?.querySelectorAll('.isometric-prefecture.atlas-curated').forEach((element) => {
        const id = element.dataset.atlasRegion;
        const selected = id === this.selectedId;
        const region = this.controller.regionById.get(id);
        element.classList.toggle('atlas-match', this.matchingIds.has(id));
        element.classList.toggle('atlas-available', (region?.availableWines || 0) > 0);
        element.classList.toggle('atlas-selected', selected);
        element.classList.toggle('atlas-filtered-out', !this.matchingIds.has(id));
        element.setAttribute('aria-pressed', String(selected));
        if (selected) element.parentNode?.appendChild(element);
      });
      this.svg?.classList.toggle('has-selection', Boolean(this.selectedId));
      this.renderSelectionPlaces();
      this.updateRegionLabel();
      this.syncElevations();
      this.syncCityVisibility();
    }

    syncElevations() {
      if (!this.svg || !this.layout) return;
      this.surfaceById?.forEach((surface, regionId) => {
        const rest = this.elevationFor(regionId, 'rest');
        const hover = this.elevationFor(regionId, 'hover');
        const selected = this.elevationFor(regionId, 'selected');
        const restOffset = this.projectedElevation(rest);
        const hoverOffset = this.projectedElevation(hover);
        const selectedOffset = this.projectedElevation(selected);
        surface.group.style.setProperty('--isometric-rest-x', `${restOffset.x}px`);
        surface.group.style.setProperty('--isometric-rest-y', `${restOffset.y}px`);
        surface.group.style.setProperty('--isometric-hover-x', `${hoverOffset.x}px`);
        surface.group.style.setProperty('--isometric-hover-y', `${hoverOffset.y}px`);
        surface.group.style.setProperty('--isometric-selected-x', `${selectedOffset.x}px`);
        surface.group.style.setProperty('--isometric-selected-y', `${selectedOffset.y}px`);
        surface.group.style.setProperty('--isometric-shadow-x', `${Math.max(1.2, this.layout.extrusionX * 8)}px`);
        surface.baseSide.setAttribute(
          'd', this.featureSidePath(surface.feature, surface.projector, this.layout.baseDepth + rest)
        );
        surface.hoverSide.setAttribute(
          'd', this.featureSidePath(surface.feature, surface.projector, this.layout.baseDepth + hover)
        );
        surface.selectedSide.setAttribute(
          'd', this.featureSidePath(surface.feature, surface.projector, this.layout.baseDepth + selected)
        );
      });

      this.svg.querySelectorAll('[data-region-ids]').forEach((place) => {
        const regionId = place.dataset.regionIds;
        const state = regionId === this.selectedId ? 'selected' : 'rest';
        const offset = this.projectedElevation(this.elevationFor(regionId, state));
        place.style.setProperty('--isometric-place-x', `${offset.x}px`);
        place.style.setProperty('--isometric-place-y', `${offset.y}px`);
      });

      if (this.regionLabel?.dataset.regionIds) {
        const labelRegionId = this.regionLabel.dataset.regionIds;
        const labelState = labelRegionId === this.selectedId ? 'selected' : 'rest';
        const labelOffset = this.projectedElevation(this.elevationFor(labelRegionId, labelState));
        this.regionLabel.style.setProperty('--isometric-label-x', `${labelOffset.x}px`);
        this.regionLabel.style.setProperty('--isometric-label-y', `${labelOffset.y}px`);
      }
    }

    setCitiesVisible(visible) {
      this.showCities = Boolean(visible);
      this.renderSelectionPlaces();
      this.syncElevations();
      this.syncCityVisibility();
    }

    syncCityVisibility() {
      if (!this.svg) return;
      const mobile = this.layout?.name === 'mobile';
      this.cityMarkers.forEach((marker) => {
        const selectedCapital = marker.role === 'capital' && marker.regionId === this.selectedId;
        const visible = marker.role === 'tokyo'
          || this.showCities
          || (!mobile && this.level !== 'japan' && selectedCapital);
        marker.group.classList.toggle('is-visible', visible);
        marker.group.classList.toggle('is-selected-capital', selectedCapital);
        marker.group.setAttribute('aria-hidden', String(!visible));
      });
      this.applyLabelCollisions();
    }

    applyLabelCollisions() {
      const occupied = [];
      if (this.regionLabelBox && this.regionLabel?.classList.contains('is-visible')) occupied.push(this.regionLabelBox);
      const fontSize = this.level === 'detail' ? 3.4 : this.level === 'region' ? 4.9 : (this.layout?.name === 'mobile' ? 7.6 : 9);
      const visible = this.cityMarkers
        .filter((marker) => marker.group.classList.contains('is-visible'))
        .sort((a, b) => this.cityPriority(a) - this.cityPriority(b));
      visible.forEach((marker) => {
        const width = Math.max(14, marker.name.length * fontSize * 0.59);
        const height = fontSize * 1.35;
        const labelX = marker.x + marker.dx;
        const labelY = marker.y + marker.dy - height;
        const box = {
          x: marker.dx < 0 ? labelX - width : labelX,
          y: labelY,
          width,
          height,
        };
        const collides = occupied.some((item) => !(
          box.x + box.width + 3 < item.x
          || item.x + item.width + 3 < box.x
          || box.y + box.height + 2 < item.y
          || item.y + item.height + 2 < box.y
        ));
        marker.label.classList.toggle('is-colliding', collides);
        if (!collides) occupied.push(box);
      });
    }

    cityPriority(marker) {
      if (marker.role === 'tokyo') return 2;
      if (marker.role === 'capital' && marker.regionId === this.selectedId) return 3;
      if (marker.role === 'capital') return 4;
      return 5;
    }

    focusCoordinate() {
      const region = this.controller.regionById.get(this.selectedId);
      if (!region) return null;
      if (region.id === 'hokkaido') {
        const sapporo = region.atlas?.capital;
        const yoichi = region.atlas?.wineHub;
        if (sapporo && yoichi) return {
          longitude: (Number(sapporo.longitude) + Number(yoichi.longitude)) / 2,
          latitude: (Number(sapporo.latitude) + Number(yoichi.latitude)) / 2,
        };
      }
      if (region.atlas?.capital) return region.atlas.capital;
      return null;
    }

    fixedCameraTarget(level) {
      const presets = CAMERA_PRESETS[this.layoutName] || CAMERA_PRESETS.desktop;
      if (level === 'japan' || !this.selectedId) return { ...this.originalViewBox };
      const preset = presets[level] || presets.region;
      const focus = this.focusCoordinate();
      const code = this.regionCode(this.selectedId);
      let anchor = focus
        ? this.projector([Number(focus.longitude), Number(focus.latitude)], code)
        : null;
      if (!anchor) {
        const bounds = this.geometryById.get(this.selectedId);
        anchor = bounds ? [bounds.x + (bounds.width / 2), bounds.y + (bounds.height / 2)] : null;
      }
      if (!anchor) return { ...this.originalViewBox };
      return {
        x: anchor[0] - (preset.width * presets.focusX),
        y: anchor[1] - (preset.height * presets.focusY),
        width: preset.width,
        height: preset.height,
      };
    }

    animateViewBox(target, animate = true) {
      if (!this.svg || !target) return;
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      const current = this.svg.viewBox.baseVal;
      const from = { x: current.x, y: current.y, width: current.width, height: current.height };
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const duration = animate && !reduced ? 460 : 0;
      const set = (box) => this.svg?.setAttribute('viewBox', `${box.x} ${box.y} ${box.width} ${box.height}`);
      if (!duration) {
        set(target);
        return;
      }
      const started = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - started) / duration);
        const eased = progress < 0.5
          ? 2 * progress * progress
          : 1 - (Math.pow(-2 * progress + 2, 2) / 2);
        set({
          x: from.x + ((target.x - from.x) * eased),
          y: from.y + ((target.y - from.y) * eased),
          width: from.width + ((target.width - from.width) * eased),
          height: from.height + ((target.height - from.height) * eased),
        });
        if (progress < 1) this.animationFrame = requestAnimationFrame(tick);
      };
      this.animationFrame = requestAnimationFrame(tick);
    }

    setLevel(level, options = {}) {
      this.level = ['japan', 'region', 'detail'].includes(level) ? level : 'japan';
      const target = this.fixedCameraTarget(this.level);
      this.svg?.setAttribute('data-atlas-level', this.level);
      this.controller.dataset.cameraPreset = this.level;
      this.controller.dataset.cameraWidth = String(target.width);
      this.updateRegionLabel();
      this.renderSelectionPlaces();
      this.syncElevations();
      this.syncCityVisibility();
      this.animateViewBox(target, options.animate !== false);
    }

    resize() {
      const next = this.getLayout().name;
      if (next === this.layoutName) return;
      const container = this.container;
      const data = this.data;
      const state = {
        selectedId: this.selectedId,
        level: this.level,
        matchingIds: this.matchingIds,
        mode: this.mode,
        showCities: this.showCities,
      };
      this.destroy();
      super.mount(container, data, state);
      this.render();
      this.update(this.matchingIds, state);
      this.setLevel(this.level, { animate: false });
    }
  }

  registry['isometric-a-2'] = IsometricArchipelagoA2Renderer;
})();
